<template>
  <b-modal :active.sync="isModalActive" has-modal-card>
    <div class="modal-card">
      <header class="modal-card-head">
        <h1 class="is-title-header modal-card-title">
          Table Configuration
        </h1>
      </header>
      <section class="modal-card-body">
        <b-field label="Table Name">
          <b-input
            v-model="localTableName"
            placeholder="Enter table name"
            class="is-medium"
          />
        </b-field>
        <div class="header-section">
          <h3 class="column-header">Columns</h3>
          <div
            v-for="(header, hIdx) in localHeaders"
            :key="hIdx"
            class="header-root"
          >
            <HeaderNode
              :header="header"
              :header-path="[hIdx]"
              @add-sub-header="addSubHeader"
              @remove-header="removeHeader"
            />
          </div>
          <b-button
            type="is-primary"
            icon-left="plus"
            @click="addHeader"
            class="mt-3"
            >Add New Column</b-button
          >
        </div>
      </section>
      <footer class="modal-card-foot is-flex is-justify-content-flex-end">
        <b-button type="is-primary" @click="submitConfig" class="mr-2"
          >Save Table Config</b-button
        >
        <b-button type="is-normal" @click="closeModal">Cancel</b-button>
      </footer>
    </div>
  </b-modal>
</template>

<script>
const HeaderNode = {
  name: "HeaderNode",
  props: {
    header: { type: Object, required: true },
    headerPath: { type: Array, required: true }
  },
  methods: {
    addSubHeader() {
      this.$emit("add-sub-header", this.headerPath);
    },
    removeHeader() {
      this.$emit("remove-header", this.headerPath);
    }
  },
  render(h) {
    const depth = this.headerPath.length;
    let placeholder = "Add Column Name";
    if (depth === 1) placeholder = "Add Column Name";
    else if (depth === 2) placeholder = "Add Column's Sub Name";
    else if (depth === 3) placeholder = `Add Column's Sub's Sub Name`;
    else if (depth === 4) placeholder = `Add Column's Sub's Sub's Sub Name`;

    const headerRowChildren = [
      h("b-input", {
        props: { value: this.header.name },
        attrs: { placeholder: placeholder },
        on: {
          input: val => {
            this.header.name = val;
          }
        },
        class: "is-medium header-input",
        style: {
          width: "100%",
          marginRight: "8px",
          marginLeft:
            this.headerPath.length > 1
              ? (this.headerPath.length - 1) * 24 + "px"
              : "0px"
        }
      })
    ];
    // Only show add button if depth is less than 4
    if (this.headerPath.length < 4) {
      headerRowChildren.push(
        h(
          "b-button",
          {
            props: { type: "is-primary", size: "is-small", iconLeft: "plus" },
            class: "ml-1",
            attrs: { title: "Add sub-header" },
            on: { click: this.addSubHeader }
          },
          [""]
        )
      );
    }
    if (this.headerPath.length > 0) {
      headerRowChildren.push(
        h(
          "b-button",
          {
            props: { type: "is-danger", size: "is-small", iconLeft: "delete" },
            class: "ml-1",
            attrs: { title: "Remove header" },
            on: { click: this.removeHeader },
            style: { marginLeft: "8px" }
          },
          [""]
        )
      );
    }
    const children = [
      h(
        "div",
        { class: "header-row is-flex is-align-items-center" },
        headerRowChildren
      )
    ];
    if (this.header.subHeaders && this.header.subHeaders.length) {
      this.header.subHeaders.forEach((sub, idx) => {
        children.push(
          h(HeaderNode, {
            props: { header: sub, headerPath: [...this.headerPath, idx] },
            on: this.$listeners,
            style: idx === 0 ? { marginTop: "8px" } : {}
          })
        );
      });
    }
    return h(
      "div",
      {
        class: "header-node",
        style: {
          marginBottom: "8px"
        }
      },
      children
    );
  }
};

export default {
  name: "TableConfig",
  components: {
    HeaderNode
  },
  props: {
    isActive: {
      type: Boolean,
      default: false
    },
    tableName: {
      type: String,
      default: ""
    },
    headers: {
      type: Array,
      default: () => [{ name: "", rules: [] }]
    },
    existingTableNames: {
      type: Array,
      default: () => []
    },
    editingIndex: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      isModalActive: this.isActive,
      localTableName: this.tableName || "",
      localHeaders:
        this.headers && this.headers.length
          ? JSON.parse(JSON.stringify(this.headers))
          : [{ name: "", rules: [] }]
    };
  },
  watch: {
    isActive(val) {
      this.isModalActive = val;
      if (val) {
        // Reset local data when modal opens
        this.localTableName = this.tableName || "";
        this.localHeaders =
          this.headers && this.headers.length
            ? JSON.parse(JSON.stringify(this.headers))
            : [{ name: "", rules: [] }];
      }
    },
    isModalActive(val) {
      if (!val) this.$emit("cancel");
    }
  },
  methods: {
    addHeader() {
      this.localHeaders.push({ name: "", rules: [] });
    },
    removeHeader(headerPath) {
      if (!headerPath.length) return;
      if (headerPath.length === 1) {
        this.localHeaders.splice(headerPath[0], 1);
        return;
      }
      let node = this.localHeaders;
      for (let i = 0; i < headerPath.length - 1; i++) {
        node = node[headerPath[i]].subHeaders;
      }
      node.splice(headerPath[headerPath.length - 1], 1);
    },
    addSubHeader(headerPath) {
      let node = this.localHeaders;
      for (let i = 0; i < headerPath.length; i++) {
        node = node[headerPath[i]];
        if (i < headerPath.length - 1) node = node.subHeaders;
      }
      if (!node.subHeaders) this.$set(node, "subHeaders", []);
      node.subHeaders.push({ name: "", rules: [] });
    },
    addRule(headerPath) {
      this.ruleTarget = { headerPath };
      this.isSelectRuleModalActive = true;
    },
    addSelectedRule(rule) {
      const { headerPath } = this.ruleTarget;
      let node = this.localHeaders;
      for (let i = 0; i < headerPath.length; i++) {
        node = node[headerPath[i]];
        if (i < headerPath.length - 1) node = node.subHeaders;
      }
      if (!node.rules) this.$set(node, "rules", []);
      node.rules.push(rule);
      this.isSelectRuleModalActive = false;
    },
    removeRule(headerPath, rIdx) {
      let node = this.localHeaders;
      for (let i = 0; i < headerPath.length; i++) {
        node = node[headerPath[i]];
        if (i < headerPath.length - 1) node = node.subHeaders;
      }
      node.rules.splice(rIdx, 1);
    },
    closeSelectRuleModal() {
      this.isSelectRuleModalActive = false;
    },
    submitConfig() {
      if (!this.localTableName || !this.localTableName.trim()) {
        this.$buefy.toast.open({
          message: "Please enter a table name",
          type: "is-warning"
        });
        return;
      }

      // Check for duplicate table names (but allow the current table name if editing)
      const isDuplicateTableName = this.existingTableNames.some(
        (name, index) => {
          const isCurrentTable =
            this.editingIndex !== null && index === this.editingIndex;
          return (
            !isCurrentTable &&
            name.toLowerCase() === this.localTableName.toLowerCase()
          );
        }
      );

      if (isDuplicateTableName) {
        this.$buefy.dialog.alert({
          title: "Validation Error",
          message: `Table name "${this.localTableName}" already exists. Please use a different name.`,
          type: "is-danger"
        });
        return;
      }

      // Check for duplicate column names at the same level
      const duplicateErrors = this.checkDuplicateColumnNames(this.localHeaders);
      if (duplicateErrors.length > 0) {
        this.$buefy.dialog.alert({
          title: "Validation Error",
          message:
            "Duplicate column names found at the same level:\n" +
            duplicateErrors.join("\n"),
          type: "is-danger"
        });
        return;
      }

      const config = {
        tableName: this.localTableName,
        headers: this.localHeaders
      };
      this.$emit("save", config);
      this.$buefy.toast.open({
        message: "Table configuration saved!",
        type: "is-success"
      });
      this.isModalActive = false;
    },
    checkDuplicateColumnNames(headers, level = 1, errors = []) {
      // Get all column names at this level
      const names = headers
        .map(h => h.name)
        .filter(name => name && name.trim());

      // Check for duplicates
      const duplicates = names.filter(
        (name, index) => names.indexOf(name) !== index
      );
      const uniqueDuplicates = [...new Set(duplicates)];

      if (uniqueDuplicates.length > 0) {
        const levelText = level === 1 ? "Column" : `Column Level ${level}`;
        uniqueDuplicates.forEach(dup => {
          errors.push(`• "${dup}" appears multiple times at ${levelText}`);
        });
      }

      // Check subHeaders recursively
      headers.forEach(header => {
        if (header.subHeaders && header.subHeaders.length) {
          this.checkDuplicateColumnNames(header.subHeaders, level + 1, errors);
        }
      });

      return errors;
    },
    closeModal() {
      this.isModalActive = false;
    }
  }
};
</script>

<style scoped>
.header-root {
  margin-bottom: 0.5rem;
}
.header-node {
  transition: box-shadow 0.2s;
}
.header-row {
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 1px 4px 0 rgba(60, 60, 60, 0.06);
  margin-bottom: 0.2rem;
  padding: 0.5rem 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.header-row:hover {
  box-shadow: 0 2px 8px 0 rgba(60, 60, 60, 0.13);
  background: #f5f7fa;
}
.header-input {
  font-weight: 500;
  font-size: 1.08rem;
}
.rule-list {
  list-style: none;
  padding-left: 0;
  margin-bottom: 0;
  margin-top: 0.2rem;
}
.rule-list li {
  border-bottom: 1px solid #eaeaea;
  padding-bottom: 4px;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
}
.rule-list li:last-child {
  border-bottom: none;
}
.modal-card-foot {
  border-top: 1px solid #eaeaea;
  background: #fafbfc;
}
.b-button {
  min-width: 110px;
}
.mb-3 {
  margin-bottom: 1.5rem;
}
.mr-2 {
  margin-right: 0.5rem;
}
.mt-3 {
  margin-top: 1.5rem;
}
.ml-1 {
  margin-left: 0.5rem;
}
.column-header {
  color: #363636;
  display: block;
  font-size: 1rem;
  font-weight: 600;
  padding-bottom: 0.5rem;
}
</style>
